import { useEffect, useState, useRef, useLayoutEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { GetAllSelfies as BLGetAllSelfies, SelectSelfie as BLSelectSelfie, DeselectSelfie as BLDeselectSelfie, DeleteSelfie as BLDeleteSelfie, ResetDinamyc as BLResetDinamyc, FormatDateToMexico, GetSelfiesPage as BLGetSelfiesPage, SendSelfiesFanIAToScreen as BLSendSelfiesFanIAToScreen, DeselectSelfieFanIA as BLDeselectSelfieFanIA, UpdateOverlayPositionAndScale as BLUpdateOverlayPositionAndScale } from './../../../../utilities/BL/SelfieFan'
import { Loading } from './../../../Commons/Loading'
import { SecondaryBase } from './../../Commons/SecondaryBase'
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../../utilities/BL/Zones'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'
import { Confirm } from './../../../Commons/Modals/Confirm'
import { FormatDateTimeToDownloadImages as BLFormatDateTimeToDownloadImages } from './../../../../utilities/BL/Commons/DatesAndTimes'
import { useSelfiePlayer } from './useSelfieFanPlayer'


// ==============================================================================
// CONFIGURACIÓN Y CONSTANTES GLOBALES
// ==============================================================================

const PAGE_SIZE = 30; 

// Tipos de Acción para el manejo de Modales
const types = {
  // --- Acciones Manuales ---
  SELECT: 1, DESELECT: 2, DELETE: 3, RESET: 4,
  IA_SHOW1: "IA_SHOW1", IA_SHOW_BOTH: "IA_SHOW_BOTH", IA_CLEAR: "IA_CLEAR",
  // --- Acciones del Reproductor ---
  PLAYER_CLEAR: "PLAYER_CLEAR",
  CONFIRM_START_LIVE: "CONFIRM_START_LIVE",
  CONFIRM_STOP_LIVE: "CONFIRM_STOP_LIVE",
  DELETE_FROM_QUEUE: "DELETE_FROM_QUEUE",
  CLEAR_QUEUE: "CLEAR_QUEUE"
};

const initialPagedState = {
  items: [],
  nextCursor: null,
  loading: false,
  exhausted: false,
};

// ==============================================================================
// COMPONENTE PRINCIPAL: SelfieFanLibrary
// ==============================================================================
const SelfieFanLibrary = () => {
  const navigate = useNavigate()
  const { showMessage } = useFloatMessage();


  // ----------------------------------------------------------------------------
  // 1. CONEXIÓN CON EL CEREBRO
  // ----------------------------------------------------------------------------
  const playerActions = {
    sendToScreenFC: async (item) => {
      const resp = await BLSelectSelfie(item);
      if (!resp.success) throw new Error(resp.message);
      return resp;
    },
    sendToScreenIA: async (payload) => {
      const resp = await BLSendSelfiesFanIAToScreen(payload);
      if (!resp.success) throw new Error(resp.message);
      return resp;
    },
    clearScreen: async () => {
      await BLDeselectSelfie();
      await BLDeselectSelfieFanIA();
    }
  };

  const handleItemShown = (id) => {
      setFan(prev => ({...prev, items: prev.items.map(x => x.id === id ? { ...x, wasShown: true } : x)}));
      setIA(prev => ({...prev, items: prev.items.map(x => x.id === id ? { ...x, wasShown: true } : x)}));
  };

  const player = useSelfiePlayer(playerActions, showMessage, handleItemShown);

  // ----------------------------------------------------------------------------
  // 2. ESTADOS VISUALES LOCALES
  // ----------------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(true);
  
  const [sortConfig, setSortConfig] = useState({ FC: 'DESC', IA: 'DESC' });

  // Modales
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [selfie, setSelfie] = useState(null); 

  // Datos
  const [fan, setFan] = useState({ ...initialPagedState }); 
  const [ia, setIA]   = useState({ ...initialPagedState }); 

  // UI IA
  const [selectedForScreen, setSelectedForScreen] = useState({ 1: null, 2: null });
  const [animationByScreen, setAnimationByScreen] = useState({ 1: 0, 2: 0 });
  const [activeShowAction, setActiveShowAction]   = useState(null); 
  const [isClearingIA, setIsClearingIA]           = useState(false);

  // Colapsables
  const fanContainerRef = useRef(null);
  const iaContainerRef  = useRef(null);
  const fanCollapsibleRef = useRef(null);
  const iaCollapsibleRef  = useRef(null);
  const [isFanCollapsed, setIsFanCollapsed] = useState(false);
  const [isIACollapsed, setIsIACollapsed]   = useState(false);

  // ============================================================================
  // ESTADOS Y REFERENCIAS PARA OVERLAY Y TRACKPAD
  // ============================================================================
  const [overlayX, setOverlayX] = useState(0);
  const [overlayY, setOverlayY] = useState(0);
  const [overlayScale, setOverlayScale] = useState(1.0);
  
  // Feedback visual de que la API se está guardando
  const [isOverlaySyncing, setIsOverlaySyncing] = useState(false);

  // Variables para la física del Drag & Drop
  const [isDraggingTrackpad, setIsDraggingTrackpad] = useState(false);
  const dragAnchorReference = useRef({ x: 0, y: 0, startOverlayX: 0, startOverlayY: 0 });

  // ----------------------------------------------------------------------------
  // EFECTO DE SINCRONIZACIÓN EN VIVO (DEBOUNCE)
  // ----------------------------------------------------------------------------
  // Este efecto "escucha" cualquier cambio en X, Y o Escala. Si el usuario deja de mover
  // los controles por 150 milisegundos, dispara la API. Así no saturamos el servidor.
  useEffect(() => {
      setIsOverlaySyncing(true); // Encendemos el foquito amarillo

      const debounceTimer = setTimeout(async () => {
          try {
              await BLUpdateOverlayPositionAndScale(overlayX, overlayY, overlayScale);
              setIsOverlaySyncing(false); // Apagamos el foquito (Verde)
          } catch (error) {
              console.error("Error sincronizando overlay en vivo:", error);
              setIsOverlaySyncing(false);
          }
      }, 150); // 150ms es el tiempo mágico de respuesta

      return () => clearTimeout(debounceTimer);
  }, [overlayX, overlayY, overlayScale]);


  // ----------------------------------------------------------------------------
  // MÉTODOS DEL TRACKPAD VIRTUAL
  // ----------------------------------------------------------------------------
  const handleTrackpadPointerDown = (event) => {
      setIsDraggingTrackpad(true);
      // Guardamos la coordenada inicial donde el usuario puso el dedo/mouse
      dragAnchorReference.current = {
          x: event.clientX || (event.touches && event.touches[0].clientX),
          y: event.clientY || (event.touches && event.touches[0].clientY),
          startOverlayX: overlayX,
          startOverlayY: overlayY
      };
      
      // Mantenemos el "foco" del puntero aunque se salga un poco del recuadro
      if (event.target.setPointerCapture) {
          event.target.setPointerCapture(event.pointerId);
      }
  };

  const handleTrackpadPointerMove = (event) => {
      if (!isDraggingTrackpad) return; // Si no está tocando, ignoramos

      const currentPointerX = event.clientX || (event.touches && event.touches[0].clientX);
      const currentPointerY = event.clientY || (event.touches && event.touches[0].clientY);
      
      // Calculamos cuánto se movió el dedo desde el inicio
      const deltaX = currentPointerX - dragAnchorReference.current.x;
      const deltaY = currentPointerY - dragAnchorReference.current.y;

      // Multiplicador de velocidad (Sensibilidad). 
      // Un arrastre corto en el celular moverá la imagen más rápido en OBS.
      const dragSensitivity = 3; 

      // // Como nuestro origen CSS es Bottom-Right:
      // // Mover el dedo a la IZQUIERDA (deltaX negativo) aumenta el Right (overlayX positivo)
      // // Mover el dedo ARRIBA (deltaY negativo) aumenta el Bottom (overlayY positivo)
      // let calculatedNewX = dragAnchorReference.current.startOverlayX - (deltaX * dragSensitivity);
      // let calculatedNewY = dragAnchorReference.current.startOverlayY - (deltaY * dragSensitivity);

      // // Bloqueamos (Clamp) los valores para que no se salgan de 0 a 1000px
      // calculatedNewX = Math.max(0, Math.min(1000, calculatedNewX));
      // calculatedNewY = Math.max(0, Math.min(1000, calculatedNewY));

      // Redondeamos para evitar enviar decimales a C#
      let calculatedNewX = Math.round(dragAnchorReference.current.startOverlayX - (deltaX * dragSensitivity));
      let calculatedNewY = Math.round(dragAnchorReference.current.startOverlayY - (deltaY * dragSensitivity));

      // // Bloqueamos (Clamp) los valores para que no se salgan de 0 a 1000px
      // calculatedNewX = Math.max(0, Math.min(1000, calculatedNewX));
      // calculatedNewY = Math.max(0, Math.min(1000, calculatedNewY));

      // LÍMITES REALES PARA 1920x1080 (OBS)
      const MAX_X = 1700; // Tope izquierdo
      const MAX_Y = 700;  // Tope superior

      // Bloqueamos (Clamp) para que no se salgan de los bordes reales
      calculatedNewX = Math.max(0, Math.min(MAX_X, calculatedNewX));
      calculatedNewY = Math.max(0, Math.min(MAX_Y, calculatedNewY));

      setOverlayX(calculatedNewX);
      setOverlayY(calculatedNewY);
  };

  const handleTrackpadPointerUp = () => {
      setIsDraggingTrackpad(false);
  };


  // ----------------------------------------------------------------------------
  // 3. HELPERS VISUALES Y LÓGICA
  // ----------------------------------------------------------------------------
  const btnClass = (base, isActive) => `${base} ${isActive ? "Btn__Adsl--Success" : ""}`;

  const getSortedItems = (items, type) => {
    if (!items || items.length === 0) return [];
    const order = sortConfig[type] || 'DESC'; 
    return [...items].sort((a, b) => {
        const idxA = (typeof a.index === 'number') ? a.index : 0;
        const idxB = (typeof b.index === 'number') ? b.index : 0;
        return order === 'ASC' ? idxA - idxB : idxB - idxA; 
    });
  };

  const setOrderFor = (type, order) => {
      setSortConfig(prev => ({ ...prev, [type]: order }));
  };

  const useCollapsibleParent = (parentRef, contentRef, isCollapsed) => {
    useLayoutEffect(() => {
      const parent = parentRef.current;
      const content = contentRef.current;
      if (!parent || !content) return;
      parent.style.overflow = "hidden";
      parent.style.transition = "max-height .25s ease";
      const setMax = () => { parent.style.maxHeight = isCollapsed ? "0px" : `${content.scrollHeight}px`; };
      setMax();
      const ro = new ResizeObserver(() => { if (!isCollapsed) parent.style.maxHeight = `${content.scrollHeight}px`; });
      ro.observe(content);
      return () => ro.disconnect();
    }, [parentRef, contentRef, isCollapsed]);
  };
  useCollapsibleParent(fanCollapsibleRef, fanContainerRef, isFanCollapsed);
  useCollapsibleParent(iaCollapsibleRef,  iaContainerRef,  isIACollapsed);

  const preserveScroll = (containerRef, updateFn) => {
    const el = containerRef.current;
    if (!el) return updateFn();
    const prevTop = el.scrollTop;
    const prevHeight = el.scrollHeight;
    updateFn();
    requestAnimationFrame(() => {
      const newHeight = el.scrollHeight;
      el.scrollTop = prevTop + (newHeight - prevHeight);
    });
  };

  const mergePage = (prev, page) => {
    const map = new Map(prev.items.map((x) => [x.id, x]));
    for (const it of page.items) map.set(it.id, { ...(map.get(it.id) || {}), ...it });
    return { items: Array.from(map.values()), nextCursor: page.nextCursor ?? null, loading: false, exhausted: !page.nextCursor };
  };

  const markLoading = (prev) => ({ ...prev, loading: true });

  const loadFirstPage = async (source) => {
    const setState = source === "selfie_fan" ? setFan : setIA;
    setState({ ...initialPagedState, loading: true });
    try {
      const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: null });
      if (resp.success) setState(mergePage(initialPagedState, { items: resp.data.items, nextCursor: resp.data.nextCursor }));
    } catch (err) { setState({ ...initialPagedState, loading: false }); showMessage({ type: MESSAGE_TYPE.BAD, text: err.message }); }
  };

  const loadMore = async (source) => {
    const state = source === "selfie_fan" ? fan : ia;
    const setState = source === "selfie_fan" ? setFan : setIA;
    const container = source === "selfie_fan" ? fanContainerRef : iaContainerRef;
    if (state.loading) return; 
    setState(markLoading);
    try {
      if (state.nextCursor) {
        const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: state.nextCursor });
        if (!resp.success) throw new Error(resp.message);
        preserveScroll(container, () => setState((prev) => mergePage(prev, { items: resp.data.items, nextCursor: resp.data.nextCursor })));
      } else {
        const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: null });
        if (!resp.success) throw new Error(resp.message);
        const existingIds = new Set(state.items.map((x) => x.id));
        const latest = state.items.length ? new Date(Math.max(...state.items.map((x) => new Date(x.createdAtUtc).getTime()))) : null;
        const incoming = resp.data.items;
        const onlyNew = incoming.filter((x) => !existingIds.has(x.id) && (!latest || new Date(x.createdAtUtc) > latest));
        if (!onlyNew.length) { setState((prev) => ({ ...prev, loading: false })); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "No hay más por ahora." }); return; }
        preserveScroll(container, () => setState(prev => ({ ...prev, items: [...prev.items, ...onlyNew], loading: false })));
      }
    } catch (err) { setState((prev) => ({ ...prev, loading: false })); showMessage({ type: MESSAGE_TYPE.BAD, text: err.message || "Error al cargar más." }); }
  };

  const reloadNow = async () => { await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Listado actualizado." }); };
  useEffect(() => { (async () => { await reloadNow(); setIsLoading(false); })(); }, []);
  
  const ReturnToDashboard = async () => {
    try {
      navigate('/admin/dashboard')
      //await BLResetCurrentDynamic()
    //} finally {
      //getOutOfTheDynamic()
    } catch {

    }
  }

  // ----------------------------------------------------------------------------
  // 4. FUNCIONES AUXILIARES (API)
  // ----------------------------------------------------------------------------
  const apiRotate = async (id) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}SelfieFan/${id}/rotate`;
      const res = await fetch(url, { method: 'POST' });
      if (!res.ok) throw new Error("Error al rotar");
      
      const timestamp = new Date().getTime(); 
      const updateImageCache = (list) => list.map(item => {
          if (item.id !== id) return item;
          const cleanUrl = item.thumbUrl.split('?')[0]; 
          const newUrl = `${cleanUrl}?t=${timestamp}`;
          return { ...item, thumbUrl: newUrl }; 
      });

      setFan(prev => ({ ...prev, items: updateImageCache(prev.items) }));
      setIA(prev => ({ ...prev, items: updateImageCache(prev.items) }));
      
      const targetItem = fan.items.find(x => x.id === id) || ia.items.find(x => x.id === id);
      if(targetItem) {
          const cleanUrl = targetItem.thumbUrl.split('?')[0];
          const newUrl = `${cleanUrl}?t=${timestamp}`;
          player.refreshItemInQueue(id, newUrl, 0); 
      }
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Imagen rotada." });
    } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: "No se pudo rotar." }); }
  };

  const apiToggleHide = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (user?.username) headers['X-Moderator-Id'] = user.username;

      const url = `${process.env.REACT_APP_API_URL}SelfieFan/${id}/toggle-hide`;
      const res = await fetch(url, { method: 'POST', headers });
      if (!res.ok) throw new Error("Error");
      const updateVisibility = (list) => list.map(item => item.id === id ? { ...item, isHidden: !item.isHidden } : item);
      setFan(prev => ({ ...prev, items: updateVisibility(prev.items) }));
      setIA(prev => ({ ...prev, items: updateVisibility(prev.items) }));
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Visibilidad actualizada." });
    } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al ocultar." }); }
  };

  const apiDownloadAll = () => {
    const url = `${process.env.REACT_APP_API_URL}SelfieFan/download-all`;
    window.open(url, '_blank');
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Generando ZIP..." });
  };

  // ----------------------------------------------------------------------------
  // 5. HANDLERS UI (Modales)
  // ----------------------------------------------------------------------------
  const handleLiveToggleRequest = () => { setModalType(player.isLive ? types.CONFIRM_STOP_LIVE : types.CONFIRM_START_LIVE); setShowConfirmModal(true); };
  const confirmLiveAction = () => { player.toggleLive(!player.isLive); setShowConfirmModal(false); };
  const handleClearScreenRequest = () => { setModalType(types.PLAYER_CLEAR); setShowConfirmModal(true); };
  const confirmClearScreen = async () => { await player.emergencyStop(); setShowConfirmModal(false); };
  const handleDeleteFromQueueRequest = (index) => { setModalData({ index }); setModalType(types.DELETE_FROM_QUEUE); setShowConfirmModal(true); };
  const confirmDeleteFromQueue = () => { if (modalData && typeof modalData.index === 'number') { player.removeFromQueue(player.activeTab, modalData.index); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Eliminado." }); } setShowConfirmModal(false); };
  const confirmClearQueue = () => { player.clearQueue(player.activeTab); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Fila vaciada." }); setShowConfirmModal(false); };
  const handleAddToQueueFC = (item) => { if (item.isHidden) return; player.addToQueue('FC', item); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Agregado." }); };
  const handleAddSetToQueueIA = () => { if (!selectedForScreen[1] || !selectedForScreen[2]) { showMessage({ type: MESSAGE_TYPE.BAD, text: "Selecciona 2 imágenes." }); return; } const item1 = ia.items.find(x => x.id === selectedForScreen[1]); const item2 = ia.items.find(x => x.id === selectedForScreen[2]); if (item1?.isHidden || item2?.isHidden) { showMessage({ type: MESSAGE_TYPE.BAD, text: "Imagen oculta." }); return; } const newSet = { id: `SET-${Date.now()}`, img1_id: selectedForScreen[1], img2_id: selectedForScreen[2], anim1: animationByScreen[1], anim2: animationByScreen[2], img1_thumb: item1?.thumbUrl, img2_thumb: item2?.thumbUrl }; player.addToQueue('IA', newSet); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Set agregado." }); setSelectedForScreen({ 1: null, 2: null }); setAnimationByScreen({ 1: 0, 2: 0 }); };
  const ShowConfirmModalLegacy = (data, type) => { setSelfie(data); setModalType(type); setShowConfirmModal(true); };
  const handleDirectDownload = async (item, index) => { try { if (!item?.contentUrl) return; const response = await fetch(`${process.env.REACT_APP_API_URL}SelfieFan/${item.id}/content`); if (!response.ok) { showMessage({ type: MESSAGE_TYPE.BAD, text: "Error." }); return; } const blob = await response.blob(); const ext = item.contentType?.includes("png") ? "png" : "jpg"; let sourceText = item.source === 'selfie_ia' ? '_IA' : ''; const formattedDateTime = BLFormatDateTimeToDownloadImages(item.createdAtUtc) || 'date'; const filename = `${index}${sourceText}_${formattedDateTime}.${ext}`; const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); } catch { showMessage({ type: MESSAGE_TYPE.BAD, text: "Error." }); } };
  const SelectSelfie = async () => { if (!selfie) return; if (selfie.isHidden) return; setSelectedForScreen({ 1: null, 2: null }); setAnimationByScreen({ 1: 0, 2: 0 }); const resp = await playerActions.sendToScreenFC(selfie); if(resp.success) { showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mostrando." }); handleItemShown(selfie.id); } setShowConfirmModal(false); };
  const DeselectSelfie = async () => { const resp = await BLDeselectSelfie(); if(resp.success) { showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Quitado." }); setFan(prev => ({...prev, items: prev.items.map(x => ({...x, isSelected: false}))})); } setShowConfirmModal(false); };
  const DeleteSelfie = async () => { if (!selfie) return; const s = selfie; if (s.source === "selfie_ia") { setIA(prev => ({ ...prev, items: prev.items.filter(x => x.id !== s.id) })); } else { setFan(prev => ({ ...prev, items: prev.items.filter(x => x.id !== s.id) })); } const resp = await BLDeleteSelfie(s); if(resp.success) showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Eliminado." }); setShowConfirmModal(false); };
  const ResetDinamyc = async () => { await BLResetCurrentDynamic(); setFan({ ...initialPagedState }); setIA({ ...initialPagedState }); player.clearQueue('FC'); player.clearQueue('IA'); await reloadNow(); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Reseteado." }); setShowConfirmModal(false); };
  const toggleCardForScreen = (screenNum, cardId) => { setSelectedForScreen(prev => { const other = screenNum === 1 ? 2 : 1; if (prev[screenNum] === cardId) { setAnimationByScreen(a => ({ ...a, [screenNum]: 0 })); return { ...prev, [screenNum]: null }; } if (prev[other] === cardId) { setAnimationByScreen(a => ({ ...a, [other]: 0 })); return { ...prev, [other]: null, [screenNum]: cardId }; } return { ...prev, [screenNum]: cardId }; }); };
  const setAnimationForScreen = (screenNum, animId) => { setAnimationByScreen(prev => ({ ...prev, [screenNum]: animId })); };
  const SendSelfiesFanIAToScreen = async (screens) => { const id1 = selectedForScreen[1]; const id2 = selectedForScreen[2]; const item1 = ia.items.find(x => x.id === id1); const item2 = ia.items.find(x => x.id === id2); if ((screens.includes(1) && item1?.isHidden) || (screens.includes(2) && item2?.isHidden)) { showMessage({ type: MESSAGE_TYPE.BAD, text: "Imagen oculta." }); return; } const dataToSend = []; if (screens.includes(1) && id1) dataToSend.push({ id: id1, animation: animationByScreen[1] || 0 }); if (screens.includes(2) && id2) dataToSend.push({ id: id2, animation: animationByScreen[2] || 0 }); if(!dataToSend.length) return; const resp = await playerActions.sendToScreenIA(dataToSend); if(resp.success) { showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Enviado." }); setActiveShowAction(screens.length === 1 ? "screen1" : "screen2"); const idsMostrados = [id1, id2].filter(x => x); idsMostrados.forEach(id => handleItemShown(id)); } setShowConfirmModal(false); };
  const ClearSelfiesFanIAFromScreen = async () => { await BLDeselectSelfieFanIA(); showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Pantalla limpia." }); setShowConfirmModal(false); };


  // ----------------------------------------------------------------------------
  // 6. RENDERIZADO (JSX)
  // ----------------------------------------------------------------------------

  if (isLoading) return <Loading />;

  const currentQItem = player.queues[player.activeTab][player.currentIndex[player.activeTab]];
  const isPauseIcon = player.playingTab === player.activeTab;

  return (
    <div className="SelfieFan">
      <SecondaryBase actionToExecute={ReturnToDashboard}>

        <p className='fs-3 text-center'><i className='bi bi-camera'></i> Biblioteca de imágenes</p>

        <div className="d-flex justify-content-center align-items-center mt-4 gap-3">
            <button type="button" className="Btn__Adsl--RedOutlineTransparent mb-0" onClick={reloadNow}><i className="bi bi-arrow-counterclockwise"></i> Actualizar listado</button>
            <button type="button" className="Btn__Adsl--RedOutlineTransparent" onClick={apiDownloadAll} title="Descargar todas las fotos en ZIP"><i className="bi bi-file-earmark-zip"></i> Descargar Todo</button>
        </div>

        {/* ===================== SECCIÓN 2: BIBLIOTECA FAN CAM ===================== */}
        <div className="SelfieFan__Admin__Section">
          <div className="d-flex justify-content-between align-items-center mb-2 px-3">
             <div className="d-flex align-items-center">
                 {/* <img src="/img/selfiefan/Adsl-FanCam.png" className="SelfieFan__Admin__Section__Logo me-3" alt="" /> */}
                 <p className="SelfieFan__Admin__Title mb-0">Selfie Fan</p>
             </div>
             <div className="d-flex align-items-center">
                 <span className="me-2 text-white small">Ordenar:</span>
                 <div className="btn-group btn-group-sm">
                     <button type="button" className={`btn ${sortConfig.FC === 'ASC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setOrderFor('FC', 'ASC')}>Ascendente</button>
                     <button type="button" className={`btn ${sortConfig.FC === 'DESC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setOrderFor('FC', 'DESC')}>Descendente</button>
                 </div>
             </div>
          </div>

          <p className="text-center">Total de fotos: {fan.items.length}</p>
          <button type="button" className="Btn__Adsl--RedOutlineTransparent mx-auto" onClick={() => setIsFanCollapsed(c => !c)}>{isFanCollapsed ? "Mostrar" : "Ocultar"} <i className={`bi ${isFanCollapsed ? "bi-caret-down-fill" : "bi-caret-up-fill"}`}></i></button>
          <div ref={fanCollapsibleRef} className="Collapsible">
            <div ref={fanContainerRef}>
              {fan.items.length === 0 ? (<p className="SelfieFan__Admin__Text--Center">Actualmente no hay fotos</p>) : (
                <>
                  <div className="SelfieFan__Admin__List">
                    {getSortedItems(fan.items, 'FC').map((it, index) => (
                      <div key={it.id} className={`SelfieFan__Admin__List__Item ${it.isHidden ? "opacity-50" : ""}`}>
                        <div className="SelfieFan__Admin__List__Item__Buttons">
                          <button type="button" className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button" onClick={() => apiRotate(it.id)} title="Rotar" disabled={it.isHidden}><i className="bi bi-arrow-clockwise"></i></button>
                          <button type="button" className={`Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button ${it.isHidden ? 'text-warning' : ''}`} onClick={() => apiToggleHide(it.id)} title={it.isHidden ? "Mostrar" : "Ocultar"}><i className={`bi ${it.isHidden ? 'bi-eye-slash-fill' : 'bi-eye'}`}></i></button>
                          {it.contentUrl && (<button type="button" className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button" onClick={() => handleDirectDownload(it, it.index || index + 1)} title="Descargar"><i className="bi bi-download"></i></button>)}
                        </div>
                        <p>#{it.index || '?'}</p>
                        <img src={it.thumbUrl} className="SelfieFan__Admin__Image--Normal" alt={`Imagen #${it.index}`} loading="lazy" key={it.thumbUrl} />
                        <p>Fecha: {FormatDateToMexico(it.createdAtUtc)}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="flex gap-2 justify-center mt-3"><button type="button" className="Btn__Adsl--RedOutlineTransparent mx-auto" onClick={() => loadMore("selfie_fan")} disabled={fan.loading}>{fan.loading ? "Cargando..." : <><i className="bi bi-arrow-counterclockwise"></i> Cargar más</>}</button></div>
            </div>
          </div>
        </div>

        {/* ===================== SECCIÓN 3: BIBLIOTECA FAN CAM IA ===================== */}
        <div className="SelfieFan__Admin__Section">
          <div className="d-flex justify-content-between align-items-center mb-2 px-3">
             <div className="d-flex align-items-center">
                 {/* <img src="/img/selfiefan/IADSL.png" className="SelfieFan__Admin__Section__Logo me-3" alt="" /> */}
                 <p className="SelfieFan__Admin__Title mb-0">Selfie Fan con IA</p>
             </div>
             <div className="d-flex align-items-center">
                 <span className="me-2 text-white small">Ordenar:</span>
                 <div className="btn-group btn-group-sm">
                     <button type="button" className={`btn ${sortConfig.IA === 'ASC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setOrderFor('IA', 'ASC')}>Ascendente</button>
                     <button type="button" className={`btn ${sortConfig.IA === 'DESC' ? 'btn-danger' : 'btn-outline-secondary text-white'}`} onClick={() => setOrderFor('IA', 'DESC')}>Descendente</button>
                 </div>
             </div>
          </div>

          <p className="text-center">Total de fotos: {ia.items.length}</p>
          <button type="button" className="Btn__Adsl--RedOutlineTransparent mx-auto" onClick={() => setIsIACollapsed(c => !c)}>{isIACollapsed ? "Mostrar" : "Ocultar"} <i className={`bi ${isIACollapsed ? "bi-caret-down-fill" : "bi-caret-up-fill"}`}></i></button>
          <div ref={iaCollapsibleRef} className="Collapsible">
            <div ref={iaContainerRef}>
              {ia.items.length === 0 ? (<p className="SelfieFan__Admin__Text--Center">Actualmente no hay fotos con IA</p>) : (
                <>
                  <div className="SelfieFanIA__Admin__List">
                    {getSortedItems(ia.items, 'IA').map((it, index) => {
                      const isFor1 = selectedForScreen[1] === it.id; const isFor2 = selectedForScreen[2] === it.id; const selectedForAny = isFor1 || isFor2;
                      return (
                        <div key={it.id} className={`SelfieFan__Admin__List__Item ${it.isHidden ? "opacity-50" : ""}`} style={it.wasShown ? {position: 'relative'} : {}}>
                          <div className="SelfieFan__Admin__List__Item__Buttons">
                            <button type="button" className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button" onClick={() => apiRotate(it.id)} title="Rotar" disabled={it.isHidden}><i className="bi bi-arrow-clockwise"></i></button>
                            <button type="button" className={`Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button ${it.isHidden ? 'text-warning' : ''}`} onClick={() => apiToggleHide(it.id)} title={it.isHidden ? "Mostrar" : "Ocultar"}><i className={`bi ${it.isHidden ? 'bi-eye-slash-fill' : 'bi-eye'}`}></i></button>
                            {it.contentUrl && (<button type="button" className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button" onClick={() => handleDirectDownload(it, it.index || index + 1)}><i className="bi bi-download"></i></button>)}
                          </div>
                          <p>#{it.index || '?'}</p>
                          <img src={it.thumbUrl} className="SelfieFan__Admin__Image--Normal" alt={`Imagen #${it.index}`} key={it.thumbUrl} />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {/* <div className="flex gap-2 justify-center mt-3"><button type="button" className="Btn__Adsl--RedOutlineTransparent mx-auto" onClick={() => loadMore("selfie_ia")} disabled={ia.loading}>{ia.loading ? "Cargando..." : "Cargar más"}</button></div> */}
              <div className="flex gap-2 justify-center mt-3"><button type="button" className="Btn__Adsl--RedOutlineTransparent mx-auto" onClick={() => loadMore("selfie_ia")} disabled={ia.loading}>{ia.loading ? "Cargando..." : <><i className="bi bi-arrow-counterclockwise"></i> Cargar más</>}</button></div>
            </div>
          </div>
        </div>

        {/* ... Modales ... */}
        {showConfirmModal && (
          <>
             {modalType === types.CONFIRM_START_LIVE && (<Confirm message="¿Deseas iniciar la proyección a la pantalla grande?" actionToYesButton={confirmLiveAction} textYesButton="Sí, iniciar" textNoButton="Cancelar" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.CONFIRM_STOP_LIVE && (<Confirm message="¿Deseas detener la proyección?" actionToYesButton={confirmLiveAction} textYesButton="Sí, detener" textNoButton="Cancelar" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.PLAYER_CLEAR && (<Confirm message="¿Estás seguro de detener la proyección y limpiar la pantalla grande?" actionToYesButton={confirmClearScreen} textYesButton="Sí, limpiar" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.DELETE_FROM_QUEUE && (<Confirm message="¿Deseas quitar esta foto de la fila de reproducción?" actionToYesButton={confirmDeleteFromQueue} textYesButton="Sí, quitar" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.CLEAR_QUEUE && (<Confirm message={`¿Estás seguro de eliminar toda la fila de reproducción ${player.activeTab && player.activeTab === 'FC' ? 'de Fan Cam' : 'Fan Cam con IA'}? (Esta acción no se puede deshacer)`} actionToYesButton={confirmClearQueue} textYesButton="Sí, eliminar todo" textNoButton="Cancelar" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             
             {modalType === types.SELECT && (<Confirm message={`¿Estás segur@ de proyectar en la pantalla la Selfie Fan (${selfie?.index || selfie?.id})?`} image={selfie?.thumbUrl} actionToYesButton={SelectSelfie} textYesButton="Sí, proyectar ahora" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.DESELECT && (<Confirm message={`¿Estás segur@ de quitar de la pantalla la Selfie Fan (${selfie?.index || selfie?.id})?`} image={selfie?.thumbUrl} actionToYesButton={DeselectSelfie} textYesButton="Sí, quitar ahora" textNoButton={"No"} setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.DELETE && (<Confirm message={`¿Estás segur@ de eliminar la Selfie (${selfie?.index || selfie?.id})?`} image={selfie?.thumbUrl} actionToYesButton={DeleteSelfie} textYesButton="Sí, eliminar" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.RESET && (<Confirm message="¿Estás segur@ de reiniciar la dinámica completa?" image={null} actionToYesButton={ResetDinamyc} textYesButton="Sí, reiniciar ahora" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.IA_SHOW1 && (<Confirm message="¿Deseas proyectar solo la Pantalla #1?" image={null} actionToYesButton={() => SendSelfiesFanIAToScreen([1])} textYesButton="Sí, proyectar 1" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.IA_SHOW_BOTH && (<Confirm message="¿Deseas proyectar la Pantalla #1 y #2?" image={null} actionToYesButton={() => SendSelfiesFanIAToScreen([1,2])} textYesButton="Sí, proyectar 1 y 2" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
             {modalType === types.IA_CLEAR && (<Confirm message="¿Deseas quitar las imágenes de la pantalla grande?" image={null} actionToYesButton={ClearSelfiesFanIAFromScreen} textYesButton="Sí, quitar ahora" textNoButton="No" setShowModal={setShowConfirmModal} setConfirmData={() => {}} />)}
          </>
        )}

      </SecondaryBase>
    </div>
  );
};

export { SelfieFanLibrary };